import { captureException } from '@sentry/react';
import { geoAlbersUsa, geoMercator, geoPath } from 'd3-geo';
import { useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { feature } from 'topojson-client';

import { MapContext } from '../context';
import {
	colorize,
	drawCountiesCanvas,
	leadingZeroFIPS,
	showSumOfPolicies,
} from '../helpers';
import { useStateGeography } from '../hooks/useStateGeography';
import { ActiveStateOrUSId, StateId, TaxData } from '../types';
import CountyTable from './CountyTable';
import HoverContainer from './HoverContainer';
import MapContainer from './MapContainer';
import './StateMap.scss';

const Container = styled.div`
	align-items: center;
	display: grid;
	grid-gap: 1rem;
	grid-template: repeat(2, auto) / auto;

	@media screen and (min-width: 600px) {
		grid-template: auto / 3fr 2fr;
	}
`;

interface Props {
	id: StateId;
	data: TaxData;
	year: number;
	activeState: StateId;
	updateActiveState: (stateId: ActiveStateOrUSId) => void;
	domain: number[];
}

const StateMap = ({
	id,
	data,
	year,
	activeState,
	updateActiveState,
	domain,
}: Props) => {
	const { geography, geographyLoading, geographyError } = useStateGeography(id);

	if (geographyLoading) {
		return null;
	}

	if (geographyError || !geography) {
		captureException(geographyError);
		return <p>Error loading map data.</p>;
	}

	return (
		<StateMapRenderer
			id={id}
			data={data}
			year={year}
			activeState={activeState}
			updateActiveState={updateActiveState}
			domain={domain}
			geography={geography}
		/>
	);
};

interface StateMapRendererProps extends Props {
	geography: NonNullable<ReturnType<typeof useStateGeography>['geography']>;
}

const StateMapRenderer = ({
	id,
	data,
	year,
	activeState,
	updateActiveState,
	domain,
	geography,
}: StateMapRendererProps) => {
	const { context } = useContext(MapContext);
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const [activeCounty, setActiveCounty] = useState(
		+Object.keys(data[activeState].data[year])[0],
	);

	const xScale = 1200;
	const yScale = 800;

	const countiesFeatures = feature(
		geography,
		geography.objects[leadingZeroFIPS(id)],
	);

	let path = geoPath().projection(
		geoMercator().fitSize([xScale, yScale], countiesFeatures),
	);

	if (id === 2 || id === 15) {
		path = geoPath().projection(
			geoAlbersUsa().fitSize([xScale, yScale], countiesFeatures),
		);
	}

	useEffect(() => {
		if (!canvasRef.current) return;
		drawCountiesCanvas({
			data,
			counties: countiesFeatures,
			year,
			path,
			canvas: canvasRef.current,
			xScale,
			yScale,
			domain,
			context,
		});
	}, [canvasRef.current, context, year]);

	if (!('features' in countiesFeatures)) {
		captureException(
			new Error('No features in countiesFeatures', {
				cause: countiesFeatures,
			}),
		);
		return null;
	}

	const counties: { id: number; name: string }[] = [];

	const countyShapes = countiesFeatures.features.map(d => {
		if (!d.properties) return null;
		const countyId = +d.properties.county;
		const countyName = d.properties.county_name;
		counties.push({ id: countyId, name: countyName });
		const countyData = data[activeState].data[year][countyId];
		const countyPath = path(d);
		if (countyData) {
			return (
				<path
					className="state-map__county"
					style={{
						stroke: countyId === activeCounty ? '#f7d' : 'transparent',
						strokeWidth: countyId === activeCounty ? 2 : 0.5,
					}}
					d={countyPath ?? undefined}
					fill={
						countyData && countyData
							? colorize(showSumOfPolicies(countyData, context), domain).hex()
							: '#888'
					}
					id={`county-detail-${countyId}`}
					key={`county-detail-${countyId}`}
					onMouseOver={() => {
						if (countyId > 0) setActiveCounty(countyId);
					}}
				/>
			);
		} else {
			return null;
		}
	});

	return (
		<>
			<Container>
				<MapContainer>
					<svg width="100%" viewBox={`0 0 ${xScale} ${yScale}`}>
						<rect
							data-tip
							data-for="go-back"
							className="state-map__bg"
							style={{
								height: yScale,
								width: xScale,
							}}
							onClick={() => updateActiveState(0)}
						/>
						{countyShapes}
					</svg>
					<canvas
						width={xScale}
						height={yScale}
						id="us-canvas"
						ref={canvasRef}
						style={{ width: '100%' }}
					></canvas>
				</MapContainer>
				<CountyTable
					data={data[activeState].data}
					year={year}
					counties={counties.sort((a, b) => a.name.localeCompare(b.name))}
					activeCounty={activeCounty}
					updateActiveCounty={setActiveCounty}
					activeState={id}
					updateActiveState={updateActiveState}
				/>
			</Container>
			<HoverContainer id="go-back">Click to return to US map.</HoverContainer>
		</>
	);
};

export default StateMap;
